<template>
  <LayoutActionCard
    :title="title"
    :subtitle="subtitle"
    :is-configured="isConfigured"
    :is-configure-active="isConfigureActive"
    :selected="selected"
    @delete="$emit('delete')"
    @configure="handleConfigure"
  >
    <template #icon>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM14 16H7V14H14V16Z" fill="#FFFFFF"/>
      </svg>
    </template>

    <template #content>
      <!-- Not Configured State -->
      <div v-if="!isConfigured" class="action-content">
        <!-- Add Internal Notes Field -->
        <!-- <div class="form-field">
          <label class="field-label">Add Internal Notes <span class="required">*</span></label>
          <TextareaWithClear
            v-model="localFormData.notes"
            placeholder="Type your notes here"
            :max-length="1000"
            :rows="5"
            @update:modelValue="updateSharedData"
          />
        </div> -->
              </div>

      <!-- Configured State -->
      <div v-else class="action-content action-content--configured">
        <div class="config-row">
          <span class="config-label">Notes: </span>
          <span class="config-value">{{ displayNotes }}</span>
        </div>
              </div>
    </template>
  </LayoutActionCard>
</template>

<script>
import LayoutActionCard from './LayoutActionCard.vue';
import TextareaWithClear from '@/components/workflow/TextareaWithClear.vue';

export default {
  name: 'AddInternalNotes',
  components: {
    LayoutActionCard,
    TextareaWithClear
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete', 'configure'],
  data() {
    return {
      localFormData: {
        notes: ''
      }
    };
  },
  computed: {
    title() {
      return 'Add Internal Notes';
    },
    subtitle() {
      return 'Create a staff-only internal note for the patient';
    },
    // Use savedData for display when available
    displayData() {
      return (this.data && this.data.savedData) ? this.data.savedData : this.data;
    },
    isConfigured() {
      return Boolean(this.displayData && this.displayData.configured);
    },
    isConfigureActive() {
      // Configure is active when notes have content
      return Boolean(this.localFormData.notes && this.localFormData.notes.trim().length > 0);
    },
    displayNotes() {
      if (this.displayData && this.displayData.notes) {
        const maxLength = 100;
        if (this.displayData.notes.length > maxLength) {
          return this.displayData.notes.substring(0, maxLength) + '...';
        }
        return this.displayData.notes;
      }
      return 'No notes added';
    }
  },
  watch: {
    'data.notes': {
      handler(newVal) {
        if (newVal !== undefined) {
          this.localFormData.notes = newVal;
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Initialize local form data from props if available
    if (this.data && this.data.notes) {
      this.localFormData.notes = this.data.notes;
    }
  },
  methods: {
    handleConfigure() {
      this.$emit('configure');
    },
    updateSharedData() {
      // Update the shared data instance directly
      this.data.notes = this.localFormData.notes;
    }
  }
};
</script>

<style scoped>
.action-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.action-content--configured {
  gap: 8px;
}

/* Form Field */
.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.field-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

/* Textarea */
.textarea-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  padding: 8px 12px;
  transition: border-color 0.2s ease;
}

.textarea-container:focus-within {
  border-color: #004879;
}

.action-textarea {
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  border: none;
  outline: none;
  resize: none;
  min-height: 90px;
}

.action-textarea::placeholder {
  color: #999;
}

.textarea-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-top: 4px;
}

.character-count {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #999;
}

.clear-btn {
  padding: 4px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1E4670;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-position: from-font;
}

.clear-btn:hover {
  color: #004879;
}

/* Configured State */
.config-row {
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.config-row:last-child {
  margin-bottom: 0;
}

.config-label {
  display: inline;
  font-weight: 700;
  color: #374957;
}

.config-value {
  display: inline;
  font-weight: 400;
  color: #333;
}

.required {
  color: #EF4444;
  font-weight: 700;
}

</style>
